import { getCustomerImageUploadUrl } from "../../../services/documentGeneration.service";
import { ZohoRelatedMeter } from "../../../services/zoho.service";
import { BulkCustomerRef, BulkMeterRef } from "../../../types/proposalDocuments.types";

export const ZOHO_ID_LENGTH = 18;
const ZOHO_ID_PATTERN = new RegExp(`^\\d{${String(ZOHO_ID_LENGTH)}}$`);

export const isValidZohoId = (id: string): boolean => ZOHO_ID_PATTERN.test(id);
export const DEFAULT_OFFERS_PER_METER = 3;
export const MIN_OFFERS_PER_METER = 1;
export const MAX_OFFERS_PER_METER = 20;

// A customer as it's being set up on the page, before it's turned into a
// BulkCustomerRef for the template request
export interface BulkCustomer {
  zohoId: string;
  customerName: string;
  offersPerMeter: number;
  customerImage: string | null;
  meters: ZohoRelatedMeter[];
  selectedMeterIds: string[];
}

// The backend needs exactly one of MPAN/MPRN per meter - anything else can't
// go into the template
export const isMeterUsable = (meter: ZohoRelatedMeter): boolean =>
  Boolean(meter.mpan) !== Boolean(meter.mprn);

const toMeterRef = (meter: ZohoRelatedMeter): BulkMeterRef =>
  meter.mpan
    ? { zoho_meter_id: meter.id, mpan: meter.mpan }
    : { zoho_meter_id: meter.id, mprn: meter.mprn ?? "" };

export const selectedMeters = (customer: BulkCustomer): ZohoRelatedMeter[] =>
  customer.meters.filter((m) => isMeterUsable(m) && customer.selectedMeterIds.includes(m.id));

export const isCustomerSendable = (customer: BulkCustomer): boolean =>
  customer.customerName.trim().length > 0 && selectedMeters(customer).length > 0;

export const toCustomerRef = (customer: BulkCustomer): BulkCustomerRef => ({
  zoho_id: customer.zohoId,
  customer_name: customer.customerName.trim(),
  offers_per_meter: customer.offersPerMeter,
  customer_image: customer.customerImage,
  meters: selectedMeters(customer).map(toMeterRef),
});

// Splits pasted text on whitespace/commas/semicolons and de-dupes, keeping order
export const parseZohoIds = (raw: string): string[] =>
  raw
    .split(/[\s,;]+/)
    .map((id) => id.trim())
    .filter((id, i, all) => id !== "" && all.indexOf(id) === i);

export const filenameFromResponse = (res: Response, fallback: string): string => {
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/.exec(disposition);
  return match?.[1] ?? fallback;
};

// xlsx files are zips, which start with "PK" - base64-encoded that's "UEsD"
const BASE64_ZIP_PREFIX = "UEsD";

// API Gateway hands back Lambda's base64 body undecoded unless the request
// matches its binaryMediaTypes, so accept either raw bytes or base64 text
export const xlsxBlobFromResponse = async (res: Response): Promise<Blob> => {
  const blob = await res.blob();
  const head = await blob.slice(0, BASE64_ZIP_PREFIX.length).text();
  if (head !== BASE64_ZIP_PREFIX) return blob;

  const binary = atob((await blob.text()).trim());
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export const downloadBlob =(blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

interface ValidationDetail {
  loc: (string | number)[];
  msg: string;
}

// Same status handling as the single proposal form, as they share a backend
export const describeHttpError = (status: number, data: unknown): string => {
  const detail = (data as { detail?: unknown } | null)?.detail;
  switch (status) {
    case 500:
      return "Server Error (500): Please report this to the dev team.";
    case 403:
      return "You do not have the correct permissions to generate bulk documents, contact the dev team to get your permissions updated.";
    case 400:
      return typeof detail === "string" ? detail : "The request was rejected.";
    case 422:
      if (Array.isArray(detail)) {
        const msg = (detail as ValidationDetail[])
          .map((d) => `${String(d.loc.at(-1))}: ${d.msg}`)
          .join(", ");
        return `Validation Error: ${msg}`;
      }
      return typeof detail === "string" ? detail : "Invalid input data.";
    default:
      return "An unexpected error occurred. Please check your connection.";
  }
};

export const readJsonSafely = async (res: Response): Promise<unknown> => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

// Presigned S3 upload, same flow as the single proposal's customer image -
// returns the s3 key to send as customer_image
export const uploadCustomerImage = async (file: File): Promise<string> => {
  const res = await getCustomerImageUploadUrl(file.type);
  if (!res.ok) {
    throw new Error(`Couldn't get upload URL: ${String(res.status)}`);
  }
  const { upload_url, s3_key } = (await res.json()) as { upload_url: string; s3_key: string };

  const uploadResponse = await fetch(upload_url, {
    method: "PUT",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${String(uploadResponse.status)}`);
  }

  return s3_key;
};
